import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
  },
  content: {
    background: "#fff", borderRadius: 12, padding: 20, width: "min(760px, 96vw)",
    boxShadow: "0 10px 30px rgba(0,0,0,.25)", border: "1px solid #e5e7eb"
  },
  title: { margin: 0, marginBottom: 8, fontSize: "1.25rem", fontWeight: 700, color: "#111827" },
  subtitle: { margin: 0, marginBottom: 16, fontSize: ".9rem", color: "#6b7280" },
  row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 },
  col: { display: "flex", flexDirection: "column" },
  label: { fontSize: ".85rem", color: "#374151", marginBottom: 6, fontWeight: 600 },
  input: {
    padding: "12px 14px", borderRadius: 8, border: "2px solid #e5e7eb",
    fontSize: ".95rem", outline: "none", width: "min(200px, 96vw)"
  },
  mensagem: {
    padding: "12px 14px", borderRadius: 8, border: "2px solid #e5e7eb",
    fontSize: ".95rem", outline: "none", width: "min(560px, 96vw)"
  },
  select: {
    padding: "12px 14px", borderRadius: 8, border: "2px solid #e5e7eb",
    fontSize: ".95rem", outline: "none", cursor: "pointer", background: "#fff"
  },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 },
  btnGhost: {
    background: "#fff", border: "1px solid #e5e7eb", color: "#374151",
    padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontWeight: 600
  },
  btnPrimary: {
    background: "#4f46e5", border: "1px solid #4338ca", color: "#fff",
    padding: "10px 14px", borderRadius: 10, cursor: "pointer", fontWeight: 700
  },
  hint: { fontSize: ".8rem", color: "#6b7280", marginTop: 6 }
};

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - initialData?: { id, id_cliente|clienteId, valor, vencimento(YYYY-MM-DD), descricao? }
 * - URL_BACKEND: string
 * - token: string
 * - emitenteId: number|string   // obrigatório
 * - descricaoDefault?: string   // opcional
 * - onSaved?: () => void
 */
export default function ModalBoleto({
  isOpen,
  onClose,
  initialData = null,
  URL_BACKEND,
  token,
  emitenteId,
  descricaoDefault = "Faturamento",
  onSaved
}) {
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [saving, setSaving] = useState(false);

  const [clienteId, setClienteId] = useState(initialData?.id_cliente || initialData?.clienteId || "");
  const [valor, setValor] = useState(initialData?.valor ?? "");
  const [vencimento, setVencimento] = useState(
    initialData?.vencimento ? String(initialData.vencimento).substring(0, 10) : ""
  );
  const [descricao, setDescricao] = useState(initialData?.descricao || descricaoDefault);

  const isEdit = useMemo(() => Boolean(initialData?.id), [initialData]);

  const truncate = (text, maxLength) => {
    if (!text) return '';
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  // Busca os clientes para mostrar no select
  useEffect(() => {
    if (!isOpen || !URL_BACKEND || !token) return;
    const fetchClientes = async () => {
      setLoadingClientes(true);
      try {
        const storedEmitenteId = localStorage.getItem('userIdEmitente');
        const { data } = await axios.get(`${URL_BACKEND}/clientes/Todos/${storedEmitenteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClientes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        toast.error("Erro ao carregar clientes.");
      } finally {
        setLoadingClientes(false);
      }
    };
    fetchClientes();
  }, [isOpen, URL_BACKEND, token]);

  useEffect(() => {
    setClienteId(initialData?.id_cliente || initialData?.clienteId || "");
    setValor(initialData?.valor ?? "");
    setVencimento(initialData?.vencimento ? String(initialData.vencimento).substring(0, 10) : "");
    setDescricao(initialData?.descricao || descricaoDefault);
  }, [initialData, isOpen, descricaoDefault]);

  if (!isOpen) return null;

  const parseValor = (v) => Number(String(v).replace(",", "."));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!URL_BACKEND || !token) return toast.error("Sessão inválida.");
    if (!emitenteId) return toast.error("Emitente inválido.");
    if (!clienteId) return toast.error("Selecione um cliente.");
    const vNum = parseValor(valor);
    if (!vNum || vNum <= 0) return toast.error("Informe um valor válido.");
    if (!vencimento) return toast.error("Informe a data de vencimento.");

    const payload = {
      id: initialData?.id || null,
      emitenteId: Number(emitenteId),
      clienteId: Number(clienteId),
      valor: vNum,
      vencimento,     // YYYY-MM-DD
      descricao: (descricao || "").trim() || "Faturamento"
    };

    setSaving(true);
    try {
      await axios.post(`${URL_BACKEND}/faturamento/save`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isEdit ? "Faturamento atualizado!" : "Faturamento incluído!");
      onClose?.();
      onSaved?.();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || "Erro ao salvar faturamento.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={S.overlay}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={S.content}>
        <h2 style={S.title}>{isEdit ? "Editar Faturamento" : "Incluir Faturamento"}</h2>
        <p style={S.subtitle}>Preencha os campos para {isEdit ? "atualizar" : "criar"} o lançamento.</p>

        <form onSubmit={handleSubmit}>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>Cliente</label>
              <select
                style={S.select}
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                disabled={loadingClientes}
                required
              >
                <option value="">{loadingClientes ? "Carregando..." : "Selecione..."}</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {truncate(c.nome || c.razaoSocial || `#${c.id}`, 70)}
                    {/* {c.nome || c.razaoSocial || `#${c.id}`} */}
                  </option>
                ))}
              </select>
              {/* <div style={S.hint}>Lista carregada de <code>/clientes</code>.</div> */}
            </div>

          </div>

          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>Vencimento</label>
              <input
                type="date"
                style={S.input}
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
                required
              />
            </div>

            <div style={S.col}>
              <label style={S.label}>Valor (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={S.input}
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0,00"
                required
              />
            </div>


          </div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>Mensagem no boleto</label>
              <input
                type="text"
                style={S.mensagem}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Boleto de Honorários ref. ao mês atual."
              />
            </div>

          </div>

          <div style={S.actions}>
            <button type="button" onClick={onClose} style={S.btnGhost} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" style={S.btnPrimary} disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
